import { ChangePassword, errorList } from '@/components/ChangePassword';
import FormFieldComponent from '@/components/FormFieldComponent';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/ui/use-toast';
import { displayError } from '@/lib/error';
import { useChangePassword } from '@/services/userService';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

const resetFormSchema = z
  .object({
    oldPassword: z.string().optional().nullish(),
    newPassword: z.string().optional().nullish(),
    retypeNewPassword: z.string().optional().nullish(),
  })

  .superRefine((data, ctx) => {
    if (!data?.oldPassword) {
      ctx.addIssue({
        message: 'Current password is required',
        path: ['oldPassword'],
        code: 'custom',
      });
    }
    if (!data?.newPassword) {
      ctx.addIssue({
        message: 'New password is required',
        path: ['newPassword'],
        code: 'custom',
      });
    }
    if (!data?.retypeNewPassword) {
      ctx.addIssue({
        message: 'New password is required',
        path: ['retypeNewPassword'],
        code: 'custom',
      });
    }
    if (data?.newPassword !== data?.retypeNewPassword) {
      ctx.addIssue({
        message: 'Passwords do not match. Please try again.',
        path: ['retypeNewPassword'],
        code: 'custom',
      });
    }
  });

export const ChangePasswordDialog = ({ onClose }: { onClose: () => void }) => {
  const form = useForm<z.infer<typeof resetFormSchema>>({
    resolver: zodResolver(resetFormSchema),
    mode: 'onBlur',
  });

  const {
    control,
    formState: { isValid },
    watch,
    getValues,
  } = form;

  const password = watch('newPassword');
  const { mutate: changePassword, status } = useChangePassword();
  const handleChangePassword = () => {
    const oldPassword = getValues('oldPassword');
    const newPassword = getValues('newPassword');
    if (oldPassword && newPassword) {
      changePassword(
        {
          data: {
            oldPassword: oldPassword,
            newPassword: newPassword,
          },
        },
        {
          onSuccess: (e) => {
            onClose();
            toast({
              description: e.message,
              duration: 5000,
            });
          },
          onError: (e) => {
            displayError(e);
          },
        }
      );
    }
  };

  return (
    <Dialog open={true} modal={true}>
      <FormProvider {...form}>
        <DialogContent className="max-w-[470px] w-full">
          <DialogHeader className="sticky top-0 bg-white z-10 pb-4">
            <DialogTitle className="md:text-[30px] text-[20px] font-semibold text-left">Change Password</DialogTitle>
            <DialogDescription className="text-placeholder md:text-base text-[12px] font-normal text-left">
              Enter the current password associated with your account.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-6 overflow-auto h-full max-h-[80vh]">
            <div className="flex flex-col gap-6 max-h-[calc(100%-15px)] overflow-scroll scrollbar-hide">
              <FormFieldComponent
                label="Password"
                required
                placeholder="Enter Password"
                control={control}
                name="oldPassword"
                type="password"
              />
              <ChangePassword />
            </div>
            <div className="w-full grid grid-cols-2 sticky bottom-0 !space-x-6 bg-white ">
              <Button onClick={onClose} variant="outline" className="text-xs md:text-sm text-primary">
                Cancel
              </Button>
              <Button
                type="submit"
                onClick={handleChangePassword}
                className="text-xs md:text-sm"
                disabled={
                  !isValid || !password || errorList.some((el) => !el.regex.test(password)) || status === 'pending'
                }
                isLoading={status === 'pending'}
              >
                Change Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </FormProvider>
    </Dialog>
  );
};
